import { useMutation, useQueryClient } from "@tanstack/react-query";

import { patientService } from "../../services/patientService";
import { ensureOk } from "../../utils/ensureOk";

export function useResendOnboardingEmail() {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (patientId: number) =>
      patientService.resendOnboardingEmail(patientId),
  });
  return { mutateAsync, isPending };
}

export function useFlagPatient() {
  const queryClient = useQueryClient();

  const { mutateAsync } = useMutation({
    mutationFn: async ({
      patientId,
      flagged,
    }: {
      patientId: string | number;
      flagged: boolean;
    }) => {
      return patientService.flagPatient({ id: patientId, flagged });
    },
    onSuccess: () => {
      // Invalidate every patient-list cache so both the regular hook
      // (`patient-list-${variant}`) and the paginated/infinite variant
      // (`patient-list-${variant}, variant, pageSize`) refetch.
      queryClient.invalidateQueries({ queryKey: ["patient-list-active"] });
      queryClient.invalidateQueries({ queryKey: ["patient-list-flagged"] });
      queryClient.invalidateQueries({ queryKey: ["patient-list-alerts"] });
      queryClient.invalidateQueries({ queryKey: ["patient-list-archived"] });
    },
  });

  return mutateAsync;
}

// EN-4960: replace a patient's ward set from the side panel.
export function useSetPatientWards() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async ({
      patientId,
      wardIds,
    }: {
      patientId: number;
      wardIds: number[];
    }) => {
      return ensureOk(
        await patientService.setPatientWards(patientId, { wardIds })
      );
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["patient-wards", variables.patientId],
      });
      queryClient.invalidateQueries({ queryKey: ["patient-list-active"] });
      queryClient.invalidateQueries({ queryKey: ["patient-list-flagged"] });
      queryClient.invalidateQueries({ queryKey: ["patient-list-alerts"] });
      queryClient.invalidateQueries({ queryKey: ["patient-list-archived"] });
    },
  });

  return { mutateAsync, isPending };
}
